import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import api from '../api/client'

/**
 * Afscherming van de documentatie (bezoekerskant).
 *
 * Drie standen, sitebreed en per categorie:
 *   public   — iedereen met de link leest mee (de standaard)
 *   password — één gedeeld wachtwoord dat de klant zelf uitdeelt
 *   team     — alleen ingelogde leden van dit account
 *
 * Ontgrendelen levert een versleuteld cookie op dat de server zet; de
 * frontend houdt zelf geen wachtwoord vast. Deze store weet alleen óf er
 * een slot is en of de bezoeker er doorheen is.
 */

export type AccessMode = 'public' | 'password' | 'team'
export type SectionAccessMode = AccessMode | 'inherit'
export type AccessVisibility = 'locked' | 'hidden'

/** Slot dat een bepaalde pagina/categorie blokkeert (uit een 403-response). */
export interface ContentLock {
  scope: string
  mode: AccessMode
  hint: string | null
  sectionId: number | null
  sectionName: string | null
}

/**
 * Herkent de 403 die de API stuurt wanneer er een slot voor staat.
 *
 * Twee valkuilen die hier bewust worden opgevangen:
 *
 * 1. De response-interceptor in api/client.ts zet alléén gelúkte antwoorden
 *    om naar camelCase; foutantwoorden gaan onbewerkt door. De velden komen
 *    hier dus in snake_case binnen. Beide spellingen lezen houdt dit werkend,
 *    ook als die transformatie ooit wél voor fouten gaat gelden.
 * 2. Alleen sleutels worden omgezet, geen waarden — de code blijft
 *    `content_locked`.
 *
 * Ging dit mis, dan viel `sectionId` terug op null, vroeg het slotscherm om
 * ontgrendeling van de héle site, en antwoordde de server "niet afgeschermd"
 * zolang de site zelf openbaar stond.
 */
export function readContentLock(error: any): ContentLock | null {
  const data = error?.response?.data
  if (error?.response?.status !== 403 || data?.code !== 'content_locked') return null

  return {
    scope: String(data.scope ?? 'site'),
    mode: (data.mode ?? 'password') as AccessMode,
    hint: data.hint ?? null,
    sectionId: data.sectionId ?? data.section_id ?? null,
    sectionName: data.sectionName ?? data.section_name ?? null,
  }
}

export const useAccessStore = defineStore('access', () => {
  const mode = ref<AccessMode>('public')
  const unlocked = ref(true)
  const hint = ref<string | null>(null)
  const isMember = ref(false)
  const isLoaded = ref(false)
  /**
   * Is er iets dat deze bezoeker zonder wachtwoord mag lezen? Zo ja, dan
   * toont de app de gewone site met alleen de openbare delen plus een
   * ontgrendel-knop, in plaats van het slotscherm over de volle breedte.
   */
  const hasPublicContent = ref(true)

  /** Sitebreed slot waar deze bezoeker niet doorheen komt. */
  const siteLocked = computed(() => isLoaded.value && !unlocked.value)

  /** Niet openbaar → niet indexeren (ook niet als het slot open staat). */
  const isProtected = computed(() => mode.value !== 'public')

  async function fetchStatus() {
    try {
      const { data } = await api.get('/access')
      mode.value = data.mode
      unlocked.value = data.unlocked
      hint.value = data.hint ?? null
      isMember.value = Boolean(data.isMember)
      hasPublicContent.value = Boolean(data.hasPublicContent)
    } catch {
      // Onbereikbaar (of een omgeving zonder tenant): niet blokkeren op iets
      // wat we niet weten — de API bewaakt de inhoud zelf.
      mode.value = 'public'
      unlocked.value = true
      hasPublicContent.value = true
    } finally {
      isLoaded.value = true
      applyRobotsMeta()
    }
  }

  /**
   * Ontgrendel een scope. `sectionId` alleen bij een categorie-slot; erft
   * die categorie van een ouder, dan opent de server de hele tak in één keer.
   * Geeft de foutcode terug (of null bij succes) zodat het formulier zelf de
   * juiste vertaalde melding kan tonen.
   */
  async function unlock(payload: {
    password: string
    remember: boolean
    sectionId?: number | null
  }): Promise<string | null> {
    try {
      await api.post('/access/unlock', {
        scope: payload.sectionId ? 'section' : 'site',
        section_id: payload.sectionId ?? null,
        password: payload.password,
        remember: payload.remember,
      })

      if (!payload.sectionId) unlocked.value = true

      return null
    } catch (err: any) {
      if (err?.response?.status === 429) return 'tooManyAttempts'
      return err?.response?.data?.code ?? 'unknown'
    }
  }

  /**
   * Een afgeschermde omgeving hoort niet in zoekmachines. De robots.txt van
   * de server is de sterkste laag; deze metatag dekt het geval dat de pagina
   * al gecrawld wordt vóór robots.txt opnieuw is opgehaald.
   */
  function applyRobotsMeta() {
    const id = 'robots-directive'
    const existing = document.getElementById(id)

    if (!isProtected.value) {
      existing?.remove()
      return
    }

    const tag = (existing as HTMLMetaElement | null) ?? document.createElement('meta')
    tag.id = id
    tag.setAttribute('name', 'robots')
    tag.setAttribute('content', 'noindex, nofollow')
    if (!existing) document.head.appendChild(tag)
  }

  /** Na een wijziging in de instellingen: status opnieuw ophalen. */
  async function refresh() {
    await fetchStatus()
  }

  /**
   * Mag dit account een eigen slot per categorie zetten (Starter-functie)?
   * Eén keer opgehaald en onthouden: het ⋯-menu van elke categorie leunt
   * hierop en dat mag geen verzoek per klik worden.
   */
  const categoryLocksAvailable = ref<boolean | null>(null)

  async function ensurePlanInfo() {
    if (categoryLocksAvailable.value !== null) return

    try {
      const { data } = await api.get('/account/access')
      categoryLocksAvailable.value = Boolean(data.categoryLocksAvailable)
    } catch {
      // Niet kunnen ophalen mag de dialoog niet blokkeren; de server wijst
      // een niet-toegestane stand alsnog af met een nette melding.
      categoryLocksAvailable.value = true
    }
  }

  return {
    mode,
    unlocked,
    hint,
    isMember,
    isLoaded,
    hasPublicContent,
    siteLocked,
    isProtected,
    categoryLocksAvailable,
    fetchStatus,
    refresh,
    unlock,
    ensurePlanInfo,
  }
})
